import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import {
  adminDeletePromoFn,
  adminListPromosFn,
  adminPromoTargetsFn,
  adminSavePromoFn,
  adminSetPromoActiveFn,
} from "@/lib/promos/promos.functions";
type AdminPromoDto = Awaited<ReturnType<typeof adminListPromosFn>>[number];

export const Route = createFileRoute("/admin/promos")({
  head: () => ({
    meta: [
      { title: "Promo Codes — CB16 Admin" },
      { name: "description", content: "Create, edit and schedule CB16 promo codes." },
      { property: "og:title", content: "Promo Codes — CB16 Admin" },
      { property: "og:description", content: "Create, edit and schedule CB16 promo codes." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PromosPage,
});

type Form = {
  /** Code being edited, or null when adding a new one. */
  editing: string | null;
  code: string;
  label: string;
  type: "percent" | "amount";
  value: string;
  isActive: boolean;
  startsAt: string;
  endsAt: string;
  minOrderValue: string;
  maxDiscount: string;
  usageLimit: string;
  eligibleProductIds: string[];
  eligibleCollectionIds: string[];
};

const EMPTY: Form = {
  editing: null, code: "", label: "", type: "percent", value: "10", isActive: true, startsAt: "", endsAt: "",
  minOrderValue: "", maxDiscount: "", usageLimit: "", eligibleProductIds: [], eligibleCollectionIds: [],
};

/** ISO → value for <input type="datetime-local"> in the admin's local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);
const optionalNumber = (v: string) => (v.trim() === "" ? null : Number(v));

function formFrom(p: AdminPromoDto): Form {
  return {
    editing: p.code, code: p.code, label: p.label, type: p.type, value: String(p.value), isActive: p.isActive,
    startsAt: toLocalInput(p.startsAt), endsAt: toLocalInput(p.endsAt),
    minOrderValue: p.minOrderValue?.toString() ?? "", maxDiscount: p.maxDiscount?.toString() ?? "",
    usageLimit: p.usageLimit?.toString() ?? "", eligibleProductIds: p.eligibleProductIds,
    eligibleCollectionIds: p.eligibleCollectionIds,
  };
}

function scheduleLabel(p: AdminPromoDto): string {
  const now = Date.now();
  if (p.startsAt && new Date(p.startsAt).getTime() > now) return "Scheduled";
  if (p.endsAt && new Date(p.endsAt).getTime() < now) return "Expired";
  if (p.usageLimit && p.usedCount >= p.usageLimit) return "Used up";
  return "";
}

function PromosPage() {
  const listFn = useServerFn(adminListPromosFn);
  const saveFn = useServerFn(adminSavePromoFn);
  const toggleFn = useServerFn(adminSetPromoActiveFn);
  const deleteFn = useServerFn(adminDeletePromoFn);
  const targetsFn = useServerFn(adminPromoTargetsFn);

  const [promos, setPromos] = useState<AdminPromoDto[] | null>(null);
  const [targets, setTargets] = useState<{ products: { id: string; name: string }[]; collections: { id: string; name: string }[] }>({ products: [], collections: [] });
  const [form, setForm] = useState<Form>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setPromos(await listFn());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load promo codes.");
      setPromos([]);
    }
  }, [listFn]);

  useEffect(() => {
    void load();
    targetsFn().then(setTargets).catch(() => {});
  }, [load, targetsFn]);

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(done);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  function save() {
    const code = form.code.trim().toUpperCase();
    void run(async () => {
      await saveFn({
        data: {
          mode: form.editing ? "edit" : "create", code, label: form.label.trim(), type: form.type, value: Number(form.value),
          isActive: form.isActive, startsAt: fromLocalInput(form.startsAt), endsAt: fromLocalInput(form.endsAt),
          minOrderValue: optionalNumber(form.minOrderValue), maxDiscount: optionalNumber(form.maxDiscount),
          usageLimit: optionalNumber(form.usageLimit), eligibleProductIds: form.eligibleProductIds,
          eligibleCollectionIds: form.eligibleCollectionIds,
        },
      });
      setForm(EMPTY);
    }, form.editing ? `${code} updated.` : `${code} created.`);
  }

  const field = "border border-black/15 px-2 py-2 text-sm";
  const lab = "text-[10px] font-semibold uppercase tracking-widest text-black/60";

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Promo codes" subtitle={promos ? `${promos.length} codes (built-in + custom)` : "Loading…"} />
      {error && <p role="alert" className="border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
      {notice && <p className="border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p>}
      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="overflow-x-auto border border-black/10 bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Discount</th>
                <th className="px-3 py-2">Rules</th>
                <th className="px-3 py-2">Used</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(promos ?? []).map((p) => {
                const sched = scheduleLabel(p);
                const rules = [
                  p.startsAt && `From ${new Date(p.startsAt).toLocaleDateString("en-IN")}`,
                  p.endsAt && `Until ${new Date(p.endsAt).toLocaleDateString("en-IN")}`,
                  p.minOrderValue && `Min ₹${p.minOrderValue}`,
                  p.maxDiscount && `Max ₹${p.maxDiscount}`,
                  (p.eligibleProductIds.length || p.eligibleCollectionIds.length) &&
                    `${p.eligibleProductIds.length} products · ${p.eligibleCollectionIds.length} collections`,
                ].filter(Boolean);
                return (
                  <tr key={p.code} className="border-b border-black/5 align-top">
                    <td className="px-3 py-2">
                      <p className="font-mono text-black">{p.code}</p>
                      <p className="text-xs text-black/50">{p.label}{p.isSystem ? " · built-in" : ""}</p>
                    </td>
                    <td className="px-3 py-2 text-black/80">{p.type === "percent" ? `${p.value}%` : `₹${p.value}`}</td>
                    <td className="px-3 py-2 text-xs text-black/60">{rules.length ? rules.join(" · ") : "No restrictions"}</td>
                    <td className="px-3 py-2 text-black/70">{p.usedCount}{p.usageLimit ? ` / ${p.usageLimit}` : ""}</td>
                    <td className="px-3 py-2">
                      {p.isActive ? <StatusPill tone="success">Active</StatusPill> : <StatusPill>Disabled</StatusPill>}
                      {sched && <p className="mt-1 text-[10px] uppercase tracking-widest text-black/50">{sched}</p>}
                    </td>
                    <td className="space-x-1 whitespace-nowrap px-3 py-2 text-right">
                      <button
                        disabled={busy}
                        onClick={() => void run(() => toggleFn({ data: { code: p.code, active: !p.isActive } }), `${p.code} ${p.isActive ? "disabled" : "enabled"}.`)}
                        className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50"
                      >
                        {p.isActive ? "Disable" : "Enable"}
                      </button>
                      <button disabled={busy} onClick={() => setForm(formFrom(p))} className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50">Edit</button>
                      <button
                        disabled={busy}
                        onClick={() => {
                          if (window.confirm(`Delete ${p.code}? Customers won't be able to use it any more. Past orders that used it are not changed.`)) {
                            void run(async () => {
                              await deleteFn({ data: { code: p.code } });
                              if (form.editing === p.code) setForm(EMPTY);
                            }, `${p.code} deleted.`);
                          }
                        }}
                        className="border border-red-200 px-2 py-1 text-[11px] text-red-700 hover:border-red-500"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {promos?.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-black/50">No promos.</td></tr>}
            </tbody>
          </table>
          <p className="border-t border-black/5 px-3 py-2 text-xs text-black/50">
            Built-in codes keep their original values until you edit them. Deleting or disabling a code stops future use; past orders are not changed.
          </p>
        </div>

        <div className="border border-black/10 bg-white p-4">
          <h3 className="text-sm font-semibold text-black">{form.editing ? `Edit ${form.editing}` : "Add custom promo"}</h3>
          <div className="mt-3 grid gap-2 text-sm">
            <label className="grid gap-1"><span className={lab}>Code</span>
              <input value={form.code} disabled={!!form.editing} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} className={`${field} uppercase`} /></label>
            <label className="grid gap-1"><span className={lab}>Label</span>
              <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} className={field} /></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-1"><span className={lab}>Type</span>
                <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as Form["type"] }))} className={field}>
                  <option value="percent">Percent</option><option value="amount">Amount (₹)</option>
                </select></label>
              <label className="grid gap-1"><span className={lab}>Value</span>
                <input type="number" min={0} value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} className={field} /></label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-1"><span className={lab}>Start (optional)</span>
                <input type="datetime-local" value={form.startsAt} onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))} className={field} /></label>
              <label className="grid gap-1"><span className={lab}>End (optional)</span>
                <input type="datetime-local" value={form.endsAt} onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))} className={field} /></label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <label className="grid gap-1"><span className={lab}>Min order ₹</span>
                <input type="number" min={0} value={form.minOrderValue} onChange={(e) => setForm((f) => ({ ...f, minOrderValue: e.target.value }))} className={field} /></label>
              <label className="grid gap-1"><span className={lab}>Max off ₹</span>
                <input type="number" min={0} value={form.maxDiscount} onChange={(e) => setForm((f) => ({ ...f, maxDiscount: e.target.value }))} className={field} /></label>
              <label className="grid gap-1"><span className={lab}>Usage limit</span>
                <input type="number" min={1} step={1} value={form.usageLimit} onChange={(e) => setForm((f) => ({ ...f, usageLimit: e.target.value }))} className={field} /></label>
            </div>
            <label className="grid gap-1"><span className={lab}>Eligible products (optional)</span>
              <select multiple value={form.eligibleProductIds} onChange={(e) => setForm((f) => ({ ...f, eligibleProductIds: Array.from(e.target.selectedOptions, (o) => o.value) }))} className={`${field} h-28`}>
                {targets.products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></label>
            <label className="grid gap-1"><span className={lab}>Eligible collections (optional)</span>
              <select multiple value={form.eligibleCollectionIds} onChange={(e) => setForm((f) => ({ ...f, eligibleCollectionIds: Array.from(e.target.selectedOptions, (o) => o.value) }))} className={`${field} h-24`}>
                {targets.collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></label>
            <p className="text-[11px] text-black/50">Leave products and collections empty to apply to the whole bag. Hold Ctrl/Cmd to pick several.</p>
            <label className="flex items-center gap-2 text-sm text-black/70">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} /> Active
            </label>
            <div className="mt-2 flex gap-2">
              <button disabled={busy} onClick={save} className="border border-black bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white disabled:opacity-60">
                {form.editing ? "Save changes" : "Add promo"}
              </button>
              {form.editing && (
                <button onClick={() => setForm(EMPTY)} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest">Cancel</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
